import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { LoginPage } from "./auth/LoginPage";
import { InvitePage } from "./auth/InvitePage";
import { AppShell } from "./components/AppShell";
import { OverviewPage } from "./pages/OverviewPage";
import { ColorsPage } from "./pages/ColorsPage";
import { AudiencePage } from "./pages/AudiencePage";
import { EngagementPage } from "./pages/EngagementPage";
import { CustomersPage } from "./pages/CustomersPage";
import { CustomerDetailPage } from "./pages/CustomerDetailPage";
import { LeadsPage } from "./pages/LeadsPage";
import { SessionsPage } from "./pages/SessionsPage";
import { SuspiciousPage } from "./pages/SuspiciousPage";
import { RenderWallPage } from "./pages/RenderWallPage";
import { AuditPage } from "./pages/AuditPage";
import { TeamPage } from "./pages/TeamPage";
import { UsagePage } from "./pages/UsagePage";
import { TemplatesPage } from "./pages/TemplatesPage";

/**
 * The gate.
 *
 * `/invite/:token` is deliberately mounted ABOVE it: the person opening an
 * invitation has no account yet, so bouncing them to a sign-in screen would
 * make the link useless — which is the one thing it must not be. Everything
 * else needs a session.
 */
function Gate() {
  const { admin, loading, adopt } = useAuth();

  if (loading) {
    return (
      <div className="login">
        <p className="empty">Checking your session…</p>
      </div>
    );
  }

  if (!admin) {
    return (
      <Routes>
        <Route path="/invite/:token" element={<InvitePage onAccepted={adopt} />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Someone already signed in who opens an invite link is almost always
          the OWNER checking that it works. Send them to the team page rather
          than letting them accept an invitation as themselves. */}
      <Route path="/invite/:token" element={<Navigate to="/team" replace />} />
      <Route element={<AppShell />}>
        <Route index element={<OverviewPage />} />
        <Route path="colours" element={<ColorsPage />} />
        <Route path="audience" element={<AudiencePage />} />
        <Route path="engagement" element={<EngagementPage />} />
        <Route path="usage" element={<UsagePage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="customers/:id" element={<CustomerDetailPage />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="suspicious" element={<SuspiciousPage />} />
        <Route path="renders" element={<RenderWallPage />} />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="team" element={<TeamPage />} />
        {/* A stale bookmark lands on the overview rather than on nothing. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </BrowserRouter>
  );
}
